import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getLocalized, getPropertyImage, getListingType } from '../../utils/propertyHelpers';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Bed, Bath, Square, MapPin, TrendingUp, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertiesSection = () => {
  const { t, language } = useLanguage();
  const currentLang = language || 'en';
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties?limit=3`);
      setProperties(response.data.properties || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setLoading(false);
      setProperties([]);
    }
  };

  const formatPrice = (price, listingType) => {
    if (listingType === 'rent') {
      if (price >= 1_000_000) return `AED ${(price / 1_000_000).toFixed(2)}M/year`;
      if (price >= 1_000) return `AED ${(price / 1_000).toFixed(0)}K/year`;
      return `AED ${price.toLocaleString()}/year`;
    }
    return `AED ${(price / 1000000).toFixed(2)}M`;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#C5A038]/20 text-[#12532c] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t({ en: 'Featured Properties', ar: 'عقارات مميزة' })}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t({ en: 'Premium Real Estate Portfolio', ar: 'محفظة عقارات فاخرة' })}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t({
              en: 'Handpicked properties across Dubai\'s most prestigious locations',
              ar: 'عقارات مختارة بعناية عبر أرقى مواقع دبي'
            })}
          </p>
          <div className="flex justify-center gap-4 mt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#12532c]">50+</div>
              <div className="text-sm text-gray-600">{t({ en: 'Properties', ar: 'عقار' })}</div>
            </div>
            <div className="border-l border-gray-300" />
            <div className="text-center">
              <div className="text-3xl font-bold text-[#12532c]">15+</div>
              <div className="text-sm text-gray-600">{t({ en: 'Locations', ar: 'موقع' })}</div>
            </div>
            <div className="border-l border-gray-300" />
            <div className="text-center">
              <div className="text-3xl font-bold text-[#12532c]">6-12%</div>
              <div className="text-sm text-gray-600">{t({ en: 'ROI Range', ar: 'نطاق عائد الاستثمار' })}</div>
            </div>
          </div>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">{t({ en: 'Loading properties...', ar: 'جاري تحميل العقارات...' })}</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">{t({ en: 'No properties available at the moment', ar: 'لا توجد عقارات متاحة في الوقت الحالي' })}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {properties.slice(0, 3).map((property) => (
              <Card
                key={property.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-0"
                onClick={() => navigate(`/properties/${property.id}`)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={getPropertyImage(property)}
                    alt={getLocalized(property.title, currentLang)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {/* Sale / Rent Badge */}
                    <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md ${
                      getListingType(property) === 'rent'
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#12532c] text-white'
                    }`}>
                      {getListingType(property) === 'rent'
                        ? t({ en: 'For Rent', ar: 'للإيجار' })
                        : t({ en: 'For Sale', ar: 'للبيع' })}
                    </div>
                    <div className="bg-[#C5A038] text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {getLocalized(property.type, currentLang)}
                    </div>
                    {property.yield && (
                      <div className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        {property.yield}
                      </div>
                    )}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="text-white font-bold text-2xl">{formatPrice(property.price, getListingType(property))}</div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#12532c] transition-colors">
                    {getLocalized(property.title, currentLang)}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">{getLocalized(property.location, currentLang)}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    {property.bedrooms && (
                      <div className="flex items-center">
                        <Bed className="w-4 h-4 mr-1" />
                        <span>{property.bedrooms}</span>
                      </div>
                    )}
                    {property.bathrooms && (
                      <div className="flex items-center">
                        <Bath className="w-4 h-4 mr-1" />
                        <span>{property.bathrooms}</span>
                      </div>
                    )}
                    {property.area && (
                      <div className="flex items-center">
                        <Square className="w-4 h-4 mr-1" />
                        <span>{property.area.toLocaleString()} sqft</span>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {getLocalized(property.description, currentLang)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-[#C5A038] hover:bg-[#b08f2f] text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/contact');
                      }}
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      {t({ en: 'Contact', ar: 'اتصل' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/properties/${property.id}`);
                      }}
                    >
                      {t({ en: 'Details', ar: 'التفاصيل' })}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View All Button */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => navigate('/properties')}
            className="bg-[#C5A038] hover:bg-[#b08f2f] text-white px-8"
          >
            {t({ en: 'View All Properties', ar: 'عرض جميع العقارات' })}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PropertiesSection;
